import { z } from 'zod';
import {
  CommissionType,
  Currency,
  FreightType,
  LoadingUnit,
  PaymentTrigger,
  PaymentType,
  PaymentUnit,
  VoyageCharterCostType,
} from '../../graphql';
import { portDataSaveSchema } from '../contracts/PortDataForm';

export const baseContractSchema = z.object({
  chartererOrgId: z.number().nullable(),
  shipownerOrgId: z.number().nullable(),
  vesselId: z.number().nullable(),
  layday: z.string().nullable(),
  cancelling: z.string().nullable(),
  paymentTime: z.number().nullable(),
  paymentTrigger: z.nativeEnum(PaymentTrigger).nullable(),
  paymentType: z.nativeEnum(PaymentType).nullable(),
  demurrage: z.number().nullable(),
  currency: z.nativeEnum(Currency).nullable(),
  name: z.string().nullable(),
  lumpsum: z.number().nullable(),
  externalId: z.string().nullable(),
});

export type BaseContractType = z.infer<typeof baseContractSchema>;

export const contractDetailsSchema = z
  .object({
    type: z.literal('coa'),
    coaId: z.number(),
    isPreliminary: z.boolean(),
  })
  .or(
    z.object({
      type: z.literal('spot'),
      onSubsUntil: z.string().datetime().nullable(),
    }),
  )
  .or(
    z.object({
      type: z.literal('inquiry'),
    }),
  );

export type ContractDetailsSchema = z.infer<typeof contractDetailsSchema>;

const voyageCharterRelatedOrderSchema = z.object({
  orderId: z.number().or(z.string()),
  coaRouteId: z.number().nullable(),
  relationId: z.number().nullable(),
  freightId: z.number().nullable(),
  freightAmount: z.number().nullable(),
  freightUnit: z.nativeEnum(PaymentUnit).nullable(),
  loadingRate: z.number().optional().nullable(),
  loadingUnit: z.nativeEnum(LoadingUnit).optional(),
  dischargingRate: z.number().optional().nullable(),
  dischargingUnit: z.nativeEnum(LoadingUnit).optional(),
  loadingOverheadDuration: z.number().optional().nullable(),
  dischargingOverheadDuration: z.number().optional().nullable(),
});

export const commissionSchema = z.object({
  id: z.number().nullable(),
  type: z.nativeEnum(CommissionType),
  value: z.number().or(z.string()),
  organizationId: z.number().nullable(),
  demurrageApplied: z.boolean().nullable(),
  extraFreightApplied: z.boolean().nullable(),
  bafApplied: z.boolean().nullable(),
  loadPortId: z.number().nullable(),
  dischargePortId: z.number().nullable(),
  assortmentId: z.number().nullable(),
  coaRouteId: z.number().nullable(),
});

const floatRegex = /[+-]?([0-9]*[.])?[0-9]+/;

export const extraFreightSchema = z.object({
  amount: z.number().or(z.string().min(1).regex(floatRegex)),
  description: z.string().nullable(),
  id: z.number().nullable(),
  name: z.string().nullable(),
  type: z.nativeEnum(FreightType).nullable(),
  ignoreInPnl: z.boolean().nullable(),
});

export const extraCostSchema = z.object({
  amount: z.number().or(z.string().min(1).regex(floatRegex)),
  description: z.string().nullable(),
  id: z.number().nullable(),
  name: z.string().nullable(),
  type: z.nativeEnum(VoyageCharterCostType).nullable(),
});

export const voyageCharterSchema = z.object({
  id: z.number().nullable(),
  contractDetails: contractDetailsSchema,
  relatedOrders: z.array(voyageCharterRelatedOrderSchema),
  baseContract: baseContractSchema,
  commissions: z.array(commissionSchema),
  extraFreights: z.array(extraFreightSchema),
  extraCosts: z.array(extraCostSchema),
  portData: portDataSaveSchema.optional(),
});

export type VoyageCharterSchema = z.infer<typeof voyageCharterSchema>;
export type VoyageCharterSchemaInput = z.input<typeof voyageCharterSchema>;
